import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiServices from '../services/ApiServices';
import { ShowDangerToast, ShowSuccessToast } from '../services/toast';
import StoreOverview from '../components/StoreOverview';
import DonationRequest from './DonationRequest';
import GeoLocation from '../components/GeoLocation';
import Drivers from '../components/Drivers';
import Calendar from '../components/Calendar';
import StoreProfile from '../components/StoreProfile';
import StoreHeader from '../components/header';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  LayoutDashboard,
  Package,
  CalendarDays,
  Users,
  MapPin,
  Settings,
  UserPlus,
  Store,
  LogOut,
  X,
  ArrowLeft,
  Clock,
  Ban,
  CalendarOff,
  CreditCard,
  Trash2,
  Plus,
  Save,
  Pencil
} from 'lucide-react';

const StoreDetailsPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTabRaw] = useState(localStorage.getItem('activeTab') || 'Overview');
  const [initialStatusFilter, setInitialStatusFilter] = useState('');

  const setActiveTab = (tab, statusFilter) => {
    setActiveTabRaw(tab);
    if (statusFilter !== undefined) {
      setInitialStatusFilter(statusFilter);
    } else {
      setInitialStatusFilter('');
    }
  };

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  const [configuration, setConfiguration] = useState({
    unavailableDates: [],
    itemRestrictions: '',
    amPickupLimit: '',
    pmPickupLimit: '',
    contactPhone: '',
    contactEmail: '',
  });
  const [restrictedItem, setRestrictedItem] = useState([]);
  const [newRestrictedItem, setNewRestrictedItem] = useState('');
  const [selectedHoliday, setSelectedHoliday] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const role = localStorage.getItem('role');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('Store Profile');
  const [storePictures, setStorePictures] = useState([]);
  const [pickupTimeRange, setPickupTimeRange] = useState({
    startTime: '',
    endTime: '',
  });
  const [pmTimeRange, setPmTimeRange] = useState({
    startTime: '',
    endTime: '',
  });
  const [dailyPickupLimit, setDailyPickupLimit] = useState('');
  const [slotDuration, setSlotDuration] = useState('120');
  const [amLabel, setAmLabel] = useState('Morning');
  const [pmLabel, setPmLabel] = useState('Afternoon');
  const [enableDeliveryContributions, setEnableDeliveryContributions] = useState(true);
  const [suggestedContributionAmount, setSuggestedContributionAmount] = useState('20');
  const [showDayOfWeekModal, setShowDayOfWeekModal] = useState(false);
  const [showSpecificDateModal, setShowSpecificDateModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  const [profile, setProfile] = useState({
    storeName: '',
    profilePicture: '',
  });

  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  useEffect(() => {
    if (store) {
      setPickupTimeRange({
        startTime: store.startTime || '9:00 AM',
        endTime: store.endTime || '12:00 PM',
      });
      setPmTimeRange({
        startTime: store.pmStartTime || '12:00 PM',
        endTime: store.pmEndTime || '5:00 PM',
      });
      setDailyPickupLimit(store.amPickupLimit || '10');

      setProfile({
        storeName: store.name || '',
        profilePicture: store.owner?.profilePicture || '',
      });
    }
  }, [store]);

  useEffect(() => {
    const fetchStoreDetails = async () => {
      try {
        const storeResponse = await ApiServices.getData(`/stores/${storeId}`);
        const data = storeResponse.data;

        setStore(data);
        setStorePictures(data.storePictures || []);
        setConfiguration({
          unavailableDates: data.unavailableDates || [],
          itemRestrictions: data.itemRestrictions || '',
          amPickupLimit: data.amPickupLimit || '',
          pmPickupLimit: data.pmPickupLimit || '',
          contactPhone: data.contactPhone || '',
          contactEmail: data.contactEmail || '',
        });

        setRestrictedItem(
          typeof data.itemRestriction === 'string'
            ? JSON.parse(data.itemRestriction)
            : data.itemRestriction || []
        );

        setLoading(false);
      } catch {
        ShowDangerToast('Failed to load store details');
        setLoading(false);
      }
    };

    fetchStoreDetails();
  }, [storeId]);

  const handleConfigurationSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      await ApiServices.putData(`/stores/${storeId}/configuration`, {
        storeId,
        unavailableDates: configuration.unavailableDates,
        itemRestrictions: configuration.itemRestrictions,
        amPickupLimit: parseInt(configuration.amPickupLimit),
        pmPickupLimit: parseInt(configuration.pmPickupLimit),
        startTime: pickupTimeRange.startTime,
        endTime: pickupTimeRange.endTime,
        pmStartTime: pmTimeRange.startTime,
        pmEndTime: pmTimeRange.endTime,
        itemRestriction: restrictedItem,
        contactPhone: configuration.contactPhone,
        contactEmail: configuration.contactEmail,
      });
      ShowSuccessToast('Store configuration updated successfully');
      const storeResponse = await ApiServices.getData(`/stores/${storeId}`);
      setStore(storeResponse.data);
      setConfiguration({
        unavailableDates: storeResponse.data.unavailableDates || [],
        itemRestrictions: storeResponse.data.itemRestrictions || '',
        amPickupLimit: storeResponse.data.amPickupLimit || 5,
        pmPickupLimit: storeResponse.data.pmPickupLimit || 5,
        contactPhone: storeResponse.data.contactPhone || '',
        contactEmail: storeResponse.data.contactEmail || '',
      });
      setRestrictedItem(
        typeof storeResponse.data.itemRestriction === 'string'
          ? JSON.parse(storeResponse.data.itemRestriction)
          : storeResponse.data.itemRestriction || []
      );
      setPickupTimeRange({
        startTime: storeResponse.data.startTime || '',
        endTime: storeResponse.data.endTime || '',
      });
      setPmTimeRange({
        startTime: storeResponse.data.pmStartTime || '',
        endTime: storeResponse.data.pmEndTime || '',
      });
    } catch {
      ShowDangerToast('Failed to update store configuration');
    }
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const period = hour < 12 ? 'AM' : 'PM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const time = `${displayHour}:${minute === 0 ? '00' : minute} ${period}`;
        times.push(time);
      }
    }
    return times;
  };

  const formatToMMDDYYYY = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) return dateStr;
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      const yyyy = dt.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
    return dateStr;
  };

  const addUnavailableDate = () => {
    if (selectedDate && !configuration.unavailableDates.includes(selectedDate)) {
      const updatedDates = [...configuration.unavailableDates, selectedDate];
      const updatedConfig = { ...configuration, unavailableDates: updatedDates };
      setConfiguration(updatedConfig);
      setSelectedDate('');
    }
  };

  const addUnavailableDay = () => {
    if (selectedDay && !configuration.unavailableDates.includes(selectedDay)) {
      const updatedDates = [...configuration.unavailableDates, selectedDay];
      const updatedConfig = { ...configuration, unavailableDates: updatedDates };
      setConfiguration(updatedConfig);
      setSelectedDay('');
    }
  };

  const addHoliday = () => {
    if (!selectedHoliday) return;

    try {
      const [, monthStr, dayStr] = selectedHoliday.split('-');
      const monthDay = `${monthStr.padStart(2, '0')}-${dayStr.padStart(2, '0')}`;
      const existing = configuration.unavailableDates || [];
      if (existing.includes(monthDay)) {
        ShowDangerToast && ShowDangerToast('Holiday already added');
        setSelectedHoliday('');
        return;
      }
      const updatedDates = [...existing, monthDay];
      const updatedConfig = { ...configuration, unavailableDates: updatedDates };
      setConfiguration(updatedConfig);
      setSelectedHoliday('');
    } catch (error) {
      console.error('Error adding holiday:', error);
      ShowDangerToast && ShowDangerToast('Failed to add holiday');
    }
  };

  const removeUnavailableDate = (index) => {
    const updatedDates = configuration.unavailableDates.filter((_, i) => i !== index);
    setConfiguration({ ...configuration, unavailableDates: updatedDates });
  };

  const addRestrictedItem = () => {
    if (newRestrictedItem.trim() === '') return;
    const updatedRestricted = [...restrictedItem, newRestrictedItem.trim()];
    setRestrictedItem(updatedRestricted);
    setNewRestrictedItem('');
  };

  const removeRestrictedItem = (index) => {
    if (!window.confirm(`Remove "${restrictedItem[index]}" from restricted items?`)) return;
    const updatedRestricted = restrictedItem.filter((_, i) => i !== index);
    setRestrictedItem(updatedRestricted);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    let formatted = '';
    if (value.length > 0) {
      formatted = value.substring(0, 3);
    }
    if (value.length > 3) {
      formatted += '-' + value.substring(3, 6);
    }
    if (value.length > 6) {
      formatted += '-' + value.substring(6, 10);
    }
    setConfiguration({ ...configuration, contactPhone: formatted });
  };

  const handleSaveUnavailableDays = async () => {
    try {
      await ApiServices.putData(`/stores/${storeId}/configuration`, {
        storeId,
        unavailableDates: configuration.unavailableDates,
        amPickupLimit: configuration.amPickupLimit,
        pmPickupLimit: configuration.pmPickupLimit,
        startTime: pickupTimeRange.startTime,
        endTime: pickupTimeRange.endTime,
        pmStartTime: pmTimeRange.startTime,
        pmEndTime: pmTimeRange.endTime,
        itemRestriction: restrictedItem,
        contactPhone: configuration.contactPhone,
        contactEmail: configuration.contactEmail,
      });
      ShowSuccessToast('Unavailable days saved successfully');
      const storeResponse = await ApiServices.getData(`/stores/${storeId}`);
      setStore(storeResponse.data);
    } catch (error) {
      ShowDangerToast('Failed to save unavailable days');
    }
  };
  const handleSaveRestrictedItems = async () => {
    try {
      await ApiServices.putData(`/stores/${storeId}/configuration`, {
        storeId,
        unavailableDates: configuration.unavailableDates,
        amPickupLimit: configuration.amPickupLimit,
        pmPickupLimit: configuration.pmPickupLimit,
        startTime: pickupTimeRange.startTime,
        endTime: pickupTimeRange.endTime,
        pmStartTime: pmTimeRange.startTime,
        pmEndTime: pmTimeRange.endTime,
        itemRestriction: restrictedItem,
        contactPhone: configuration.contactPhone,
        contactEmail: configuration.contactEmail,
      });
      ShowSuccessToast('Restricted items saved successfully');
      const storeResponse = await ApiServices.getData(`/stores/${storeId}`);
      setStore(storeResponse.data);
    } catch (error) {
      ShowDangerToast('Failed to save restricted items');
    }
  };

  const tabDisplayNames = {
    'Overview': 'Overview',
    'Donations': 'Pickups',
    'Calendar': 'Calendar',
    'Driver Coverage Area': 'Coverage',
    'Drivers': 'Drivers',
    'Profile & Configuration': 'Settings',
    'Add and Assign Manager': 'Manager',
    'Add Store': 'Add Store',
    'Sign Out': 'Sign Out',
  };

  const tabIcons = {
    'Overview': LayoutDashboard,
    'Donations': Package,
    'Calendar': CalendarDays,
    'Driver Coverage Area': MapPin,
    'Drivers': Users,
    'Profile & Configuration': Settings,
    'Add and Assign Manager': UserPlus,
    'Add Store': Store,
    'Sign Out': LogOut,
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'Overview':
        return <StoreOverview store={store} storePictures={storePictures} profile={profile} setActiveTab={setActiveTab} storeId={storeId} />;
      case 'Driver Coverage Area':
        return <GeoLocation storeId={storeId} store={store} profile={profile} />;
      case 'Drivers':
        return <Drivers storeId={storeId} profile={profile} />;
      case 'Donations':
        return (
          <div className="bg-gray-50 rounded-xl">
            <DonationRequest storeId={storeId} store={store} profile={profile} initialStatusFilter={initialStatusFilter} />
          </div>
        );
      case 'Add and Assign Manager':
        return (
          <div className="bg-gray-50 rounded-xl">
            <AddManager storeId={storeId} />
          </div>
        );
      case 'Add Store':
        return (
          <div className="bg-gray-50 rounded-xl">
            <AddStore ownerEmail={store?.owner?.email || ''} />
          </div>
        );
      case 'Calendar':
        return (
          <div className="bg-gray-50 rounded-xl">
            <Calendar setActiveTab={setActiveTab} profile={profile} />
          </div>
        );
      case 'Profile & Configuration':
        const settingsTabs = [
          { name: 'Store Profile' },
          { name: 'Scheduling' },
          { name: 'Restricted Items' },
          { name: 'Unavailable Days' },
          { name: 'Payments' },
        ];

        const timeOptions = generateTimeOptions();

        const getUnavailableDateDisplay = (date) => {
          if (daysOfWeek.includes(date)) {
            return { name: `Every ${date}`, type: 'Day Of Week' };
          }
          if (/^\d{2}-\d{2}$/.test(date)) {
            const [month, day] = date.split('-');
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            return { name: `${monthNames[parseInt(month) - 1]} ${parseInt(day)}`, type: 'Holiday Recurring' };
          }
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return { name: formatToMMDDYYYY(date), type: 'Specific Date' };
          }
          return { name: date, type: 'Other' };
        };

        return (
          <div className="max-w-[800px] mx-auto">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-navy-800">Settings</h2>
              <p className="text-sm text-gray-500 mt-0.5">Configure your store and pickup settings</p>
            </div>

            <div className="flex gap-0 border-b border-gray-200 mb-6 overflow-x-auto scrollbar-hide -mx-1 px-1">
              {settingsTabs.map(tab => (
                <button
                  key={tab.name}
                  onClick={() => setSettingsTab(tab.name)}
                  className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${settingsTab === tab.name
                    ? 'border-navy-600 text-navy-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            {settingsTab === 'Store Profile' && (
              <StoreProfile storeId={storeId} store={store} onProfileChange={setProfile} />
            )}

            {settingsTab === 'Scheduling' && (
              <form onSubmit={handleConfigurationSubmit}>
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                  <div className="flex items-center gap-2.5 mb-1">
                    <Clock className="w-5 h-5 text-navy-600" />
                    <h3 className="text-base font-semibold text-navy-800">Scheduling Rules</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-6 ml-[30px]">Configure pickup windows and limits</p>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Daily Pickup Limit</label>
                        <input
                          type="number"
                          value={dailyPickupLimit}
                          onChange={(e) => {
                            setDailyPickupLimit(e.target.value);
                            setConfiguration({ ...configuration, amPickupLimit: e.target.value, pmPickupLimit: e.target.value });
                          }}
                          className="input-field"
                          min="0"
                          required
                        />
                        <p className="text-xs text-green-600 mt-1">Max pickups per day</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Slot Duration (minutes)</label>
                        <select
                          value={slotDuration}
                          onChange={(e) => setSlotDuration(e.target.value)}
                          className="input-field"
                        >
                          <option value="30">30 minutes</option>
                          <option value="60">60 minutes</option>
                          <option value="90">90 minutes</option>
                          <option value="120">120 minutes</option>
                          <option value="150">150 minutes</option>
                          <option value="180">180 minutes</option>
                        </select>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-5">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">AM Window</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Label</label>
                          <input
                            type="text"
                            value={amLabel}
                            onChange={(e) => setAmLabel(e.target.value)}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Time</label>
                          <select
                            value={pickupTimeRange.startTime || "9:00 AM"}
                            onChange={(e) =>
                              setPickupTimeRange({ ...pickupTimeRange, startTime: e.target.value })
                            }
                            className="input-field"
                          >
                            {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">End Time</label>
                          <select
                            value={pickupTimeRange.endTime || "12:00 PM"}
                            onChange={(e) =>
                              setPickupTimeRange({ ...pickupTimeRange, endTime: e.target.value })
                            }
                            className="input-field"
                          >
                            {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-5">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">PM Window</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Label</label>
                          <input
                            type="text"
                            value={pmLabel}
                            onChange={(e) => setPmLabel(e.target.value)}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Time</label>
                          <select
                            value={pmTimeRange.startTime || "12:00 PM"}
                            onChange={(e) =>
                              setPmTimeRange({ ...pmTimeRange, startTime: e.target.value })
                            }
                            className="input-field"
                          >
                            {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">End Time</label>
                          <select
                            value={pmTimeRange.endTime || "5:00 PM"}
                            onChange={(e) =>
                              setPmTimeRange({ ...pmTimeRange, endTime: e.target.value })
                            }
                            className="input-field"
                          >
                            {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                    <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors">
                      <Save className="w-4 h-4" />
                      Save Settings
                    </button>
                  </div>
                </div>
              </form>
            )}

            {settingsTab === 'Restricted Items' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center gap-2.5 mb-1">
                  <Ban className="w-5 h-5 text-navy-600" />
                  <h3 className="text-base font-semibold text-navy-800">Restricted Items</h3>
                </div>
                <p className="text-sm text-gray-500 mb-5 ml-[30px]">Items that cannot be accepted for donation</p>

                <div className="flex gap-2 mb-5">
                  <input
                    type="text"
                    value={newRestrictedItem}
                    onChange={(e) => setNewRestrictedItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRestrictedItem(); } }}
                    placeholder="Enter item name..."
                    className="input-field flex-1"
                  />
                  <button
                    type="button"
                    onClick={addRestrictedItem}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {restrictedItem.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-800"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => removeRestrictedItem(index)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>

                {restrictedItem.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-8">No restricted items added yet</p>
                )}
                <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleSaveRestrictedItems}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Restricted Items
                  </button>
                </div>
              </div>
            )}

            {settingsTab === 'Unavailable Days' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center gap-2.5 mb-1">
                  <CalendarOff className="w-5 h-5 text-navy-600" />
                  <h3 className="text-base font-semibold text-navy-800">Unavailable Days</h3>
                </div>
                <p className="text-sm text-gray-500 mb-5 ml-[30px]">Days when pickups are not available</p>

                <div className="flex flex-wrap gap-3 mb-6">
                  <button
                    type="button"
                    onClick={() => setShowDayOfWeekModal(!showDayOfWeekModal)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Day of Week
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSpecificDateModal(!showSpecificDateModal)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Specific Date
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHolidayModal(!showHolidayModal)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Recurring Holiday
                  </button>
                </div>

                {showDayOfWeekModal && (
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedDay}
                        onChange={e => setSelectedDay(e.target.value)}
                        className="input-field text-sm flex-1"
                      >
                        <option value="">Select a day</option>
                        {daysOfWeek.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => { addUnavailableDay(); setShowDayOfWeekModal(false); }}
                        disabled={!selectedDay}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {showSpecificDateModal && (
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={e => setSelectedDate(e.target.value)}
                        className="input-field text-sm flex-1"
                        min={new Date().toISOString().split("T")[0]}
                      />
                      <button
                        type="button"
                        onClick={() => { addUnavailableDate(); setShowSpecificDateModal(false); }}
                        disabled={!selectedDate}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {showHolidayModal && (
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={selectedHoliday}
                        onChange={e => setSelectedHoliday(e.target.value)}
                        className="input-field text-sm flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => { addHoliday(); setShowHolidayModal(false); }}
                        disabled={!selectedHoliday}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-0 divide-y divide-gray-100">
                  {configuration.unavailableDates.map((date, index) => {
                    const display = getUnavailableDateDisplay(date);
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between py-3 px-1"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-800">{display.name}</p>
                          <p className="text-xs text-gray-500">{display.type}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeUnavailableDate(index)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {configuration.unavailableDates.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-8">No unavailable days added yet</p>
                )}

                <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleSaveUnavailableDays}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Unavailable Days
                  </button>
                </div>
              </div>
            )}

            {settingsTab === 'Payments' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center gap-2.5 mb-1">
                  <CreditCard className="w-5 h-5 text-navy-600" />
                  <h3 className="text-base font-semibold text-navy-800">Payment Settings</h3>
                </div>
                <p className="text-sm text-gray-500 mb-5 ml-[30px]">Configure payment processing and contributions</p>

                <div className="border border-gray-200 rounded-xl p-5 mb-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">Stripe Connect</h4>
                      <p className="text-sm text-gray-500 mt-0.5">Accept payments for delivery contributions</p>
                    </div>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${store.stripeOnboardingComplete
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}>
                      {store.stripeOnboardingComplete ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>

                  {!store.stripeAccountId && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await ApiServices.postData(`/stores/${storeId}/create-stripe-account`);
                          const res = await ApiServices.getData(`/stores/${storeId}/stripe-onboarding-link`);
                          window.location.href = res.data.url;
                        } catch (err) {
                          ShowDangerToast("Failed to connect Stripe");
                        }
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Connect Stripe
                    </button>
                  )}

                  {store.stripeAccountId && !store.stripeOnboardingComplete && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await ApiServices.getData(`/stores/${storeId}/stripe-onboarding-link`);
                          window.location.href = res.data.url;
                        } catch (err) {
                          ShowDangerToast("Failed to resume setup");
                        }
                      }}
                      className="px-4 py-2 bg-brand-orange text-white rounded-lg font-medium text-sm hover:bg-brand-orange-dark transition-colors"
                    >
                      Complete Stripe Setup
                    </button>
                  )}

                  {store.stripeOnboardingComplete && (
                    <p className="text-sm text-green-600 font-medium">
                      Verify that Payments and Payouts are Enabled in your Stripe Dashboard.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between py-4 border-t border-gray-100">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">Enable Delivery Contributions</h4>
                    <p className="text-sm text-gray-500 mt-0.5">Allow donors to optionally contribute to delivery costs</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEnableDeliveryContributions(!enableDeliveryContributions)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enableDeliveryContributions ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableDeliveryContributions ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                <div className="pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Suggested Contribution Amount ($)</label>
                  <input
                    type="number"
                    value={suggestedContributionAmount}
                    onChange={(e) => setSuggestedContributionAmount(e.target.value)}
                    className="input-field w-32"
                    min="0"
                  />
                </div>

                <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleConfigurationSubmit}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save Settings
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case 'Sign Out':
        return null;
      default:
        return null;
    }
  };

  const tabs = [
    { name: 'Overview', visible: true },
    { name: 'Donations', visible: true },
    { name: 'Calendar', visible: role === 'store_owner' || role === "store_manager" },
    { name: 'Drivers', visible: role === 'store_owner' || role === "store_manager" },
    { name: 'Driver Coverage Area', visible: role === 'store_owner' || role === "store_manager" },
    { name: 'Profile & Configuration', visible: role === 'store_owner' || role === "store_manager" },
    { name: 'Add and Assign Manager', visible: role === 'store_owner' },
    { name: 'Add Store', visible: role === 'store_owner' },
    { name: 'Sign Out', visible: role === 'store_owner' || role === "store_manager" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-navy-200 border-t-navy-500 rounded-full animate-spin"></div>
          <span className="text-slate-500 text-sm font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600">
        Store not found
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside
        className={`fixed top-0 left-0 h-screen w-64 bg-white border-r border-gray-200 z-40 flex flex-col transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/logo-icon.png"
                alt="Donor Dispatch"
                className="h-8 w-auto"
              />
              <span className="font-bold text-navy-700 text-sm tracking-tight">Donor Dispatch</span>
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="md:hidden p-1 rounded-lg hover:bg-gray-100 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-4 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <img
              src={profile?.profilePicture || 'https://thumbs.dreamstime.com/b/default-profile-picture-icon-high-resolution-high-resolution-default-profile-picture-icon-symbolizing-no-display-picture-360913197.jpg'}
              alt={profile?.storeName || 'Store'}
              className="w-10 h-10 rounded-lg object-cover border border-gray-200"
              draggable={false}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">
                {profile?.storeName || 'Store Name'}
              </div>
              <div className="text-xs text-slate-400 truncate">
                {role === 'store_owner' ? 'Owner' : role === 'store_manager' ? 'Manager' : 'Admin'}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-hide">
          <div className="space-y-1">
            {tabs.filter(tab => tab.visible && tab.name !== 'Sign Out').map(tab => {
              const Icon = tabIcons[tab.name] || LayoutDashboard;
              const isActive = activeTab === tab.name;
              const displayName = tabDisplayNames[tab.name] || tab.name;
              return (
                <button
                  key={tab.name}
                  onClick={() => {
                    setActiveTab(tab.name);
                    setIsMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${isActive
                    ? 'bg-navy-500 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                >
                  <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                  <span className="truncate">{displayName}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-3 border-t border-gray-100">
          <button
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-all duration-150"
          >
            <LogOut className="w-[18px] h-[18px]" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        <StoreHeader
          activeTab={tabDisplayNames[activeTab] || activeTab}
          profileImage={profile?.profilePicture}
          onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
          showMenuButton={true}
        />

        <main className="flex-1 p-4 md:p-6 lg:p-8 mt-16 overflow-y-auto">
          {role === 'superadmin' && (
            <button
              onClick={() => navigate('/admin')}
              className="btn-outline mb-4 flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Admin Panel
            </button>
          )}
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default StoreDetailsPage;

const AddManager = ({ storeId }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    assignToStore: true,
  });
  const [existingManager, setExistingManager] = useState(null);
  const [managerLoading, setManagerLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchManager = async () => {
      try {
        const storeResponse = await ApiServices.getData(`/stores/${storeId}`);
        const managerId = storeResponse.data.managerId;
        if (managerId) {
          const managerResponse = await ApiServices.getData(`/users/manager/${managerId}`);
          setExistingManager(managerResponse.data);
          setFormData({
            username: managerResponse.data.username || '',
            email: managerResponse.data.email || '',
            password: '',
            assignToStore: true,
          });
        }
      } catch (error) {
        ShowDangerToast('Failed to load manager details');
      } finally {
        setManagerLoading(false);
      }
    };
    fetchManager();
  }, [storeId]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: 'store_manager',
      };
      if (formData.assignToStore) {
        data.storeId = storeId;
      }
      const response = await ApiServices.postData('/users/create-manager', data);
      ShowSuccessToast(`Manager "${formData.username}" added successfully!`);
      setExistingManager(response.data);
      setFormData({
        username: '',
        email: '',
        password: '',
        assignToStore: true,
      });
    } catch (error) {
      ShowDangerToast('Failed to add manager');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        username: formData.username,
        email: formData.email,
      };
      if (formData.password) {
        data.password = formData.password;
      }
      await ApiServices.putData(`/users/update-manager/${existingManager.id}`, data);
      ShowSuccessToast(`Manager "${formData.username}" updated successfully!`);
      const updatedResponse = await ApiServices.getData(`/users/manager/${existingManager.id}`);
      setExistingManager(updatedResponse.data);
      setFormData({
        username: updatedResponse.data.username || '',
        email: updatedResponse.data.email || '',
        password: '',
        assignToStore: true,
      });
      setIsEditing(false);
    } catch (error) {
      ShowDangerToast('Failed to update manager');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this manager? This action cannot be undone.')) {
      try {
        await ApiServices.deleteData(`/users/delete-manager/${existingManager.id}`);
        ShowSuccessToast('Manager deleted successfully!');
        setExistingManager(null);
        setFormData({
          username: '',
          email: '',
          password: '',
          assignToStore: true,
        });
        setIsEditing(false);
      } catch (error) {
        ShowDangerToast('Failed to delete manager');
      }
    }
  };

  if (managerLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manager</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage your store's assigned manager.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gray-200" />
              <div>
                <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-48 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manager</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {existingManager ? 'View and manage your store manager.' : 'Add a manager to help run your store.'}
          </p>
        </div>
        {existingManager && !isEditing && (
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-navy-50 text-navy-700 hover:bg-navy-100 border border-navy-200 transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          </div>
        )}
      </div>

      {existingManager && !isEditing ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            {existingManager.profilePicture ? (
              <img
                src={existingManager.profilePicture}
                alt={existingManager.username}
                className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-navy-100 text-navy-600 flex items-center justify-center flex-shrink-0 text-xl font-bold">
                {existingManager.username ? existingManager.username.charAt(0).toUpperCase() : 'M'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{existingManager.username}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{existingManager.email}</p>
              <span className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                Active Manager
              </span>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Username</label>
              <p className="input-field bg-gray-50 text-gray-700">{existingManager.username}</p>
            </div>
            <div>
              <label className="input-label">Email</label>
              <p className="input-field bg-gray-50 text-gray-700">{existingManager.email}</p>
            </div>
          </div>
        </div>
      ) : existingManager && isEditing ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-5">
          <div>
            <h3 className="text-base font-semibold text-navy-800">Edit Manager</h3>
            <p className="text-sm text-gray-500 mt-0.5">Leave the password field blank to keep the current password.</p>
          </div>
          <form onSubmit={handleUpdate} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">Username <span className="text-orange-500">*</span></label>
                <input
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="input-label">Email <span className="text-orange-500">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>
            </div>
            <div>
              <label className="input-label">New Password</label>
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter new password (optional)"
                autoComplete="new-password"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                className="btn-primary px-5"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    username: existingManager.username || '',
                    email: existingManager.email || '',
                    password: '',
                    assignToStore: true,
                  });
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-5">
          <div>
            <h3 className="text-base font-semibold text-navy-800">Add a New Manager</h3>
            <p className="text-sm text-gray-500 mt-0.5">Create a manager account and assign them to this store.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">Username <span className="text-orange-500">*</span></label>
                <input
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g. johndoe"
                  required
                />
              </div>
              <div>
                <label className="input-label">Email <span className="text-orange-500">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="manager@example.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="input-label">Password <span className="text-orange-500">*</span></label>
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="input-field"
                placeholder="Create a secure password"
                autoComplete="new-password"
                required
              />
            </div>
            <div className="flex items-center gap-2.5">
              <input
                type="checkbox"
                name="assignToStore"
                checked={formData.assignToStore}
                onChange={handleChange}
                id="assignToStore"
                className="h-4 w-4 text-navy-500 border-gray-300 rounded focus:ring-navy-500 cursor-pointer"
              />
              <label
                htmlFor="assignToStore"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Assign to this store immediately
              </label>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                className="btn-primary px-5"
              >
                Add Manager
              </button>
              <button
                type="reset"
                onClick={() =>
                  setFormData({
                    username: '',
                    email: '',
                    password: '',
                    assignToStore: true,
                  })
                }
                className="px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Input = ({ name, label, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label
      htmlFor={name}
      className="block mb-1 text-sm font-medium text-gray-700"
    >
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={type !== 'password'}
      className="input-field"
    />
  </div>
);

mapboxgl.accessToken = 'pk.eyJ1IjoiZG9ub3JkaXNwYXRjaCIsImEiOiJjbWMwdjZrZWIwNnU2MmxwdHBsaGkzcm9rIn0.u70VsaQCG6q1r8SLZ8nVLg';

const AddStore = ({ ownerEmail }) => {
  const [formData, setFormData] = useState({
    name: '',
    ownerEmail: ownerEmail || '',
    ownerPassword: '',
    username: '',
    ownerProfilePicture: null,
    storePictures: [],
  });

  const [geofence, setGeofence] = useState({
    address: '',
    latitude: '',
    longitude: '',
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logoPreview = useRef(null);
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteTimeoutRef = useRef(null);

  useEffect(() => {
    if (geofence.latitude && geofence.longitude && !mapRef.current) {
      const lat = parseFloat(geofence.latitude);
      const lng = parseFloat(geofence.longitude);
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [lng, lat],
        zoom: 13,
      });
      mapRef.current.addControl(new mapboxgl.NavigationControl());
      markerRef.current = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(mapRef.current);
    }
  }, [geofence.latitude, geofence.longitude]);

  useEffect(() => {
    if (mapRef.current && markerRef.current && geofence.latitude && geofence.longitude) {
      const lat = parseFloat(geofence.latitude);
      const lng = parseFloat(geofence.longitude);
      mapRef.current.flyTo({ center: [lng, lat], zoom: 13 });
      markerRef.current.setLngLat([lng, lat]);
    }
  }, [geofence.latitude, geofence.longitude]);

  const handleAddressChange = async (e) => {
    const query = e.target.value;
    setGeofence({ ...geofence, address: query });
    if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    autocompleteTimeoutRef.current = setTimeout(async () => {
      setIsAutocompleteLoading(true);
      try {
        const { data } = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
          { params: { access_token: mapboxgl.accessToken, limit: 5 } }
        );
        setAddressSuggestions(
          data.features.map(f => ({
            formattedAddress: f.place_name,
            latitude: f.geometry.coordinates[1],
            longitude: f.geometry.coordinates[0],
          }))
        );
      } catch {
        setAddressSuggestions([]);
      } finally {
        setIsAutocompleteLoading(false);
      }
    }, 300);
  };

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, ownerProfilePicture: file });
      if (logoPreview.current) URL.revokeObjectURL(logoPreview.current);
      logoPreview.current = URL.createObjectURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!geofence.address || !geofence.latitude || !geofence.longitude) {
      ShowDangerToast('Please select an address.');
      return;
    }
    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k === 'storePictures') v.forEach(file => data.append(k, file));
        else if (v) data.append(k, v);
      });
      data.append('address', geofence.address);
      data.append('latitude', geofence.latitude);
      data.append('longitude', geofence.longitude);

      const response = await ApiServices.postFormData('/stores', data);
      const newStore = response.data;

      ShowSuccessToast('Store created successfully');

      const storedStores = localStorage.getItem('stores');
      let updatedStores = storedStores ? JSON.parse(storedStores) : [];
      updatedStores.push(newStore);
      localStorage.setItem('stores', JSON.stringify(updatedStores));

      localStorage.setItem('selectedStoreId', newStore.id.toString());
      window.location.href = `/store/${newStore.id}`;

    } catch {
      ShowDangerToast('Failed to create store');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileInputRef = useRef(null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Add New Store</h2>
        <p className="text-sm text-gray-500 mt-0.5">Create a new store location for your organization.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-5">
          <h3 className="text-base font-semibold text-navy-800">Store Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Store Name <span className="text-orange-500">*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="input-field"
                placeholder="e.g. Downtown Thrift"
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="input-label">Owner Email</label>
              <input
                type="email"
                name="ownerEmail"
                value={formData.ownerEmail}
                className="input-field bg-gray-50 text-gray-500"
                readOnly
              />
            </div>
          </div>

          <div>
            <label className="input-label">Store Logo</label>
            <div className="flex items-center gap-4 mt-1">
              {logoPreview.current ? (
                <img
                  src={logoPreview.current}
                  alt="Logo preview"
                  className="h-16 w-16 object-contain rounded-xl border border-gray-200 bg-gray-50"
                />
              ) : (
                <div className="h-16 w-16 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                  <Store className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-navy-50 text-navy-700 hover:bg-navy-100 border border-navy-200 transition-colors"
                >
                  <Store className="w-4 h-4" />
                  {logoPreview.current ? 'Change Logo' : 'Upload Logo'}
                </button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
                <p className="text-xs text-gray-400">PNG, JPG up to 2MB</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-4">
          <h3 className="text-base font-semibold text-navy-800">Location</h3>
          <div className="relative">
            <label className="input-label">Address <span className="text-orange-500">*</span></label>
            <input
              type="text"
              value={geofence.address}
              onChange={handleAddressChange}
              className="input-field"
              placeholder="Start typing to search..."
              required
              autoComplete="off"
            />
            {addressSuggestions.length > 0 && (
              <ul className="absolute z-50 border border-gray-200 rounded-lg mt-1 w-full max-h-60 overflow-auto bg-white shadow-lg">
                {isAutocompleteLoading && (
                  <li className="px-3 py-2 text-sm italic text-gray-500">Loading suggestions...</li>
                )}
                {addressSuggestions.map((address, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setGeofence({
                        ...geofence,
                        address: address.formattedAddress,
                        latitude: address.latitude,
                        longitude: address.longitude,
                      });
                      setAddressSuggestions([]);
                    }}
                    className="px-3 py-2 cursor-pointer text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                  >
                    {address.formattedAddress}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {geofence.latitude && geofence.longitude && (
            <div
              ref={mapContainerRef}
              className="w-full h-[320px] rounded-lg overflow-hidden border border-gray-200"
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary px-6"
          >
            {isSubmitting ? 'Creating...' : 'Create Store'}
          </button>
        </div>
      </form>
    </div>
  );
};